import React, { useMemo, useState } from "react";
import { AlertTriangle, Loader2, X, LogOut, CheckCircle2, FileDown } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";

/**
 * MoveOutModal — guided flow.
 * Steps compressed in one modal for speed:
 *  1. Confirm move-out date
 *  2. Enter deductions (with note) + refund amount
 *  3. Confirm & apply — cancels future rent, adds ledger entries, closes lease, archives tenant
 */
export default function MoveOutModal({ tenant, currentBalance = 0, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [moveOutDate, setMoveOutDate] = useState(new Date().toISOString().slice(0, 10));
  const [deductions, setDeductions] = useState(0);
  const [deductionNotes, setDeductionNotes] = useState("");
  const [refund, setRefund] = useState(currentBalance || 0);
  const [archive, setArchive] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const heldBefore = Number(currentBalance || 0);
  const netToRefund = useMemo(() => {
    const r = Math.max(0, heldBefore - Number(deductions || 0));
    return Math.round(r * 100) / 100;
  }, [heldBefore, deductions]);

  const applyRefundSuggestion = () => setRefund(netToRefund);

  const doMoveOut = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenant.id}/move-out`, {
        move_out_date: moveOutDate,
        deductions: Number(deductions || 0),
        deduction_notes: deductionNotes || "",
        refund_amount: Number(refund || 0),
        archive_tenant: !!archive,
      });
      toast.success(`Move-out complete · ${data.future_payments_removed} future rent(s) removed`);
      // Auto-download the signed move-out report PDF
      try {
        const token = localStorage.getItem("roost_token");
        const res = await fetch(`${API}/tenants/${tenant.id}/move-out-report`, {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `move-out-report-${tenant.full_name.replace(/\s+/g, "_")}-${moveOutDate}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
          toast.success("Move-out report PDF downloaded");
        }
      } catch { /* non-blocking */ }
      onDone?.();
      onClose?.();
    } catch {
      toast.error("Could not complete move-out");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="move-out-modal">
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-xl fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-slate-900 text-white grid place-items-center">
              <LogOut className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg">Move out {tenant.full_name}</h3>
              <p className="text-xs text-slate-500">Step {step} of 3</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          {step === 1 && (
            <>
              <div className="text-sm text-slate-700">Choose the tenant's official move-out date. All future unpaid rent AFTER this month will be removed from the rent roll.</div>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Move-out date</span>
                <input
                  type="date" value={moveOutDate}
                  onChange={(e) => setMoveOutDate(e.target.value)}
                  data-testid="move-out-date-input"
                  className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
                />
              </label>
              <div className="text-xs text-slate-500">
                Current lease ends {formatDate(tenant.end_date)}. New end date will be {formatDate(moveOutDate)}.
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                <Row label="Deposit balance held" value={formatCurrency(heldBefore)} bold />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Deductions (R)</span>
                  <input
                    type="number" step="0.01" min="0" value={deductions}
                    onChange={(e) => setDeductions(e.target.value)}
                    data-testid="move-out-deductions-input"
                    className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Refund to tenant (R)</span>
                  <div className="flex gap-2">
                    <input
                      type="number" step="0.01" min="0" value={refund}
                      onChange={(e) => setRefund(e.target.value)}
                      data-testid="move-out-refund-input"
                      className="flex-1 h-10 px-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
                    />
                    <button type="button" onClick={applyRefundSuggestion} className="h-10 px-3 rounded-lg border border-slate-200 text-xs font-medium hover:bg-slate-50">Auto</button>
                  </div>
                </label>
              </div>

              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Deduction notes</span>
                <textarea
                  value={deductionNotes}
                  onChange={(e) => setDeductionNotes(e.target.value)}
                  data-testid="move-out-notes-input"
                  className="w-full h-20 px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
                  placeholder="Cleaning fees, broken window, unpaid utilities…"
                />
              </label>

              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
                Suggested refund after deductions: <span className="font-mono-plex font-semibold text-slate-900">{formatCurrency(netToRefund)}</span>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
                <div className="flex items-start gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-900">Please review the move-out summary. This will close the lease and cannot be undone from the UI.</div>
                </div>
                <div className="divide-y divide-amber-200/60 text-sm">
                  <Row label="Tenant" value={tenant.full_name} />
                  <Row label="Move-out date" value={formatDate(moveOutDate)} />
                  <Row label="Deposit held" value={formatCurrency(heldBefore)} />
                  <Row label="Deductions" value={formatCurrency(deductions)} accent="red" />
                  <Row label="Refund to tenant" value={formatCurrency(refund)} bold accent="emerald" />
                  <Row label="Archive tenant" value={archive ? "Yes" : "No"} />
                </div>
              </div>

              <div className="rounded-lg bg-slate-100/60 border border-slate-200 p-3 text-xs text-slate-600 flex items-start gap-2">
                <FileDown className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                <span>A signed move-out report PDF (deductions + refund + signature blocks for both parties) will be downloaded automatically once you confirm.</span>
              </div>

              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox" checked={archive}
                  onChange={(e) => setArchive(e.target.checked)}
                  data-testid="move-out-archive-checkbox"
                  className="mt-0.5 accent-slate-900"
                />
                <span>Archive tenant (hide from active listings)</span>
              </label>

              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox" checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  data-testid="move-out-confirm-checkbox"
                  className="mt-0.5 accent-slate-900"
                />
                <span>I confirm the move-out details above are correct.</span>
              </label>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-between gap-2">
          {step > 1 ? (
            <button onClick={() => setStep(step - 1)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Back</button>
          ) : (
            <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Cancel</button>
          )}
          {step < 3 ? (
            <button onClick={() => setStep(step + 1)} data-testid="move-out-next-btn" className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">Next</button>
          ) : (
            <button
              onClick={doMoveOut}
              disabled={!confirmed || busy}
              data-testid="move-out-confirm-btn"
              className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Complete move-out
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold = false, accent }) {
  const color = accent === "emerald" ? "text-emerald-700" : accent === "red" ? "text-red-700" : "text-slate-900";
  return (
    <div className="flex items-center justify-between px-4 py-2.5">
      <span className="text-sm text-slate-500">{label}</span>
      <span className={`font-mono-plex ${bold ? "font-display text-lg font-semibold" : "text-sm"} ${color}`}>{value}</span>
    </div>
  );
}
